import { useState, useMemo, useCallback } from "react";
import { DateValueType } from "react-tailwindcss-datepicker";
import { DateTime } from "luxon";
import { useSearchWithParams, useParams } from "@/hooks/useSearchParams";

interface NotesFilterInput {
  search?: string;
  priority?: string[];
  visibility?: string;
  authorId?: string;
  dateRange?: { from: string; to: string };
  notableType?: string[];
  notableId?: string;
}

interface NotesOrderByInput {
  field: string;
  direction: string;
}

interface UseNotesTableFiltersOptions {
  currentUserId?: string | null;
}

export interface NotesTableFilterState {
  searchValue: string;
  setSearchValue: (value: string) => void;
  clearSearch: () => void;
  priorityFilter: string[];
  setPriorityFilter: (values: string[]) => void;
  visibilityFilter: string;
  setVisibilityFilter: (value: string) => void;
  sourceTypeFilter: string[];
  setSourceTypeFilter: (values: string[]) => void;
  dateRange: DateValueType;
  setDateRange: (value: DateValueType) => void;
  showMyNotes: boolean;
  setShowMyNotes: (value: boolean) => void;
  inputVariables: NotesFilterInput;
  filterState: Record<string, boolean>;
  activeFilterCount: number;
  handleClearAllFilters: () => void;
  buildOrderByVariable: (sortingState: { id: string; desc: boolean }[]) => NotesOrderByInput | undefined;
}

const SORT_FIELD_MAP: Record<string, string> = {
  displayTitle: "TITLE",
  content: "CONTENT",
  source: "NOTABLE_TYPE",
  authorName: "AUTHOR_NAME",
  updatedAt: "UPDATED_AT",
  priority: "PRIORITY",
  priorityExpirationDate: "PRIORITY_EXPIRATION_DATE",
  visibility: "VISIBILITY"
};

function dateValueToISO(date: DateValueType, field: "startDate" | "endDate"): string | null {
  if (!date) return null;
  const val = date[field];
  if (!val) return null;
  if (val instanceof Date) return val.toISOString().split("T")[0];
  if (typeof val === "string") {
    const dt = DateTime.fromISO(val, { zone: "utc" });
    return dt.isValid ? dt.toISODate() : null;
  }
  return null;
}

export default function useNotesTableFilters({
  currentUserId
}: UseNotesTableFiltersOptions = {}): NotesTableFilterState {
  const { params, addSearchParam, deleteSearchParams } = useParams();

  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "noteSearch",
    debounceMs: 300
  });

  const defaultPriority = params["notePriority"]
    ? Array.isArray(params["notePriority"])
      ? params["notePriority"]
      : (params["notePriority"] as string).split(",")
    : [];
  const defaultVisibility = (params["noteVisibility"] as string) || "all";
  const defaultShowMyNotes = params["noteShowMine"] === "true";
  const defaultDateRange: DateValueType = params["noteDateRange"]
    ? (() => {
        const parts = (params["noteDateRange"] as string).split(",");
        return parts.length === 2 ? { startDate: parts[0], endDate: parts[1] } : { startDate: null, endDate: null };
      })()
    : { startDate: null, endDate: null };

  const defaultSourceType = params["noteSourceType"]
    ? Array.isArray(params["noteSourceType"])
      ? params["noteSourceType"]
      : (params["noteSourceType"] as string).split(",")
    : [];

  const [priorityFilter, setPriorityFilterState] = useState<string[]>(defaultPriority);
  const [visibilityFilter, setVisibilityFilterState] = useState<string>(defaultVisibility);
  const [sourceTypeFilter, setSourceTypeFilterState] = useState<string[]>(defaultSourceType);
  const [dateRange, setDateRangeState] = useState<DateValueType>(defaultDateRange);
  const [showMyNotes, setShowMyNotesState] = useState(defaultShowMyNotes);

  const setPriorityFilter = useCallback(
    (values: string[]) => {
      setPriorityFilterState(values);
      if (values.length > 0) {
        addSearchParam("notePriority[]", values.join(","));
      } else {
        deleteSearchParams("notePriority[]");
      }
    },
    [addSearchParam, deleteSearchParams]
  );

  const setVisibilityFilter = useCallback(
    (value: string) => {
      setVisibilityFilterState(value);
      if (value !== "all") {
        addSearchParam("noteVisibility", value);
      } else {
        deleteSearchParams("noteVisibility");
      }
    },
    [addSearchParam, deleteSearchParams]
  );

  const setSourceTypeFilter = useCallback(
    (values: string[]) => {
      setSourceTypeFilterState(values);
      if (values.length > 0) {
        addSearchParam("noteSourceType[]", values.join(","));
      } else {
        deleteSearchParams("noteSourceType[]");
      }
    },
    [addSearchParam, deleteSearchParams]
  );

  const setDateRange = useCallback(
    (value: DateValueType) => {
      setDateRangeState(value);
      const start = dateValueToISO(value, "startDate");
      const end = dateValueToISO(value, "endDate");
      if (start && end) {
        addSearchParam("noteDateRange", [start, end].join(","));
      } else {
        deleteSearchParams("noteDateRange");
      }
    },
    [addSearchParam, deleteSearchParams]
  );

  const setShowMyNotes = useCallback(
    (value: boolean) => {
      setShowMyNotesState(value);
      if (value) {
        addSearchParam("noteShowMine", "true");
      } else {
        deleteSearchParams("noteShowMine");
      }
    },
    [addSearchParam, deleteSearchParams]
  );

  const filterState: Record<string, boolean> = {
    priority: priorityFilter.length > 0,
    visibility: visibilityFilter !== "all",
    sourceType: sourceTypeFilter.length > 0,
    dateRange: dateValueToISO(dateRange, "startDate") !== null
  };

  const activeFilterCount = Object.values(filterState).filter(Boolean).length;

  const inputVariables = useMemo<NotesFilterInput>(() => {
    const input: NotesFilterInput = {};

    if (debouncedSearch) {
      input.search = debouncedSearch;
    }

    if (priorityFilter.length > 0) {
      input.priority = priorityFilter;
    }

    if (visibilityFilter !== "all") {
      input.visibility = visibilityFilter;
    }

    if (showMyNotes && currentUserId) {
      input.authorId = currentUserId;
    }

    const startDate = dateValueToISO(dateRange, "startDate");
    const endDate = dateValueToISO(dateRange, "endDate");
    if (startDate && endDate) {
      input.dateRange = { from: startDate, to: endDate };
    }

    return input;
  }, [debouncedSearch, priorityFilter, visibilityFilter, showMyNotes, currentUserId, dateRange]);

  const handleClearAllFilters = useCallback(() => {
    clearSearch();
    setPriorityFilterState([]);
    setVisibilityFilterState("all");
    setSourceTypeFilterState([]);
    setDateRangeState({ startDate: null, endDate: null });
    setShowMyNotesState(false);
    deleteSearchParams(["notePriority[]", "noteVisibility", "noteSourceType[]", "noteDateRange", "noteShowMine"]);
  }, [clearSearch, deleteSearchParams]);

  const buildOrderByVariable = useCallback(
    (sortingState: { id: string; desc: boolean }[]): NotesOrderByInput | undefined => {
      if (!sortingState.length) return undefined;
      const { id, desc } = sortingState[0];
      const field = SORT_FIELD_MAP[id];
      if (!field) return undefined;
      return { field, direction: desc ? "DESC" : "ASC" };
    },
    []
  );

  return {
    searchValue,
    setSearchValue,
    clearSearch,
    priorityFilter,
    setPriorityFilter,
    visibilityFilter,
    setVisibilityFilter,
    sourceTypeFilter,
    setSourceTypeFilter,
    dateRange,
    setDateRange,
    showMyNotes,
    setShowMyNotes,
    inputVariables,
    filterState,
    activeFilterCount,
    handleClearAllFilters,
    buildOrderByVariable
  };
}
